// ============================================
// ArdSettingsMenu.jsx
// Settings popup for the on-chart ARD STRIKE shadow detector. Tunes the
// client-side ArdDetector that paints the would-fire triangles — NOT the
// server runner's own instance (that has its own CFG.strike). Same dark
// toolbar look as the AVWAP / Value Area menus — the shared menu shell
// (menu/ChartMenu) supplies frame, fit, outside-click / Esc and styles.
//
// Each knob is a ‹ value › stepper over preset stops (compact, touch-safe).
// Changes apply LIVE (onUpdate on every step) — the detector reads its config
// each tick, so retuned gates take effect on the next swap. Persisted by the
// chart to localStorage 'solcheddar:ardConfig'.
//
// config shape = ArdDetector partial-config overrides (only the tuned keys).
// ============================================
import { useState, useEffect } from 'react'
import ChartMenu, { MenuSection, MenuRow, MenuStepper, MenuToggle, MenuActions, MenuButton } from './menu/ChartMenu'

// Preset stops per knob. First-class defaults MUST match ArdDetector.js.
export const ARD_DEFAULTS = {
  minRangePct: 0.02, minSwapsPerSec: 1.0,
  eventSizeX: 25, minEventSol: 1.0,
  responseMovePct: 0.006, responseVelPctPerSec: 0.0015,
  valueBandPct: 0.006, hvnTopFrac: 0.25,
  requireValueNode: false,
}

const pct  = (v) => `${(v * 100).toFixed(v * 100 < 1 ? 2 : (v * 100 < 10 ? 1 : 0))}%`
const pctS = (v) => `${(v * 100).toFixed(2)}%/s`

const GROUPS = [
  { title: 'Trigger', knobs: [
    { key: 'minRangePct',    label: 'Volatility', fmt: pct,  opts: [0.01, 0.015, 0.02, 0.03, 0.05], tip: 'Realized range over the vol window before a strike is even considered' },
    { key: 'minSwapsPerSec', label: 'Activity',   fmt: (v) => `${v}/s`, opts: [0.5, 1.0, 2.0, 3.0], tip: 'Minimum swaps/sec — how busy the tape must be' },
  ] },
  { title: 'Aggression', knobs: [
    { key: 'eventSizeX',  label: 'Size',  fmt: (v) => `${v}×`, opts: [15, 20, 25, 40, 60], tip: 'A single print ≥ this × the active median size is an aggression event' },
    { key: 'minEventSol', label: 'Floor', fmt: (v) => `${v}◎`, opts: [0.5, 1.0, 2.0, 5.0], tip: 'Absolute SOL floor so tiny coins can’t trip it' },
  ] },
  { title: 'Response', knobs: [
    { key: 'responseMovePct',      label: 'Markup', fmt: pct,  opts: [0.004, 0.006, 0.010, 0.015], tip: 'Divergent markup from the event price that confirms absorption' },
    { key: 'responseVelPctPerSec', label: 'Speed',  fmt: pctS, opts: [0.001, 0.0015, 0.0025, 0.004], tip: 'Markup velocity — speed is a hard gate (slow moves decline)' },
  ] },
  { title: 'Value node', knobs: [
    { key: 'valueBandPct', label: 'Band',  fmt: pct, opts: [0.004, 0.006, 0.010, 0.015], tip: 'How close to a high-volume node counts as “at value”' },
    { key: 'hvnTopFrac',   label: 'Nodes', fmt: pct, opts: [0.15, 0.25, 0.40], tip: 'Top fraction of volume buckets treated as value nodes' },
  ] },
]

export default function ArdSettingsMenu({ show, x, y, openLeft, config, onUpdate, onClose }) {
  const [cfg, setCfg] = useState({ ...ARD_DEFAULTS, ...config })

  useEffect(() => { if (show) setCfg({ ...ARD_DEFAULTS, ...config }) }, [show, config])

  if (!show) return null

  // Live apply: mutate local, push the whole override set up immediately.
  const set = (key, val) => { const next = { ...cfg, [key]: val }; setCfg(next); onUpdate(next) }
  const resetAll = () => { setCfg({ ...ARD_DEFAULTS }); onUpdate({ ...ARD_DEFAULTS }) }

  return (
    <ChartMenu anchor={{ x, y }} openLeft={openLeft} onClose={onClose} width={208}
      title={<span className="cm-title-ard">ARD STRIKE <small>· shadow</small></span>}>
      {GROUPS.map((g) => (
        <MenuSection key={g.title} title={g.title}>
          {g.knobs.map((k) => (
            <MenuRow key={k.key} split label={k.label} title={k.tip}>
              <MenuStepper value={cfg[k.key] ?? ARD_DEFAULTS[k.key]} options={k.opts} format={k.fmt}
                onChange={(v) => set(k.key, v)} />
            </MenuRow>
          ))}
        </MenuSection>
      ))}

      <MenuSection>
        <MenuRow split label="Require value node"
          title="Phase 1 leaves the value gate OPEN to learn; Phase 2 requires price to sit at a value node">
          <MenuToggle on={cfg.requireValueNode} onChange={() => set('requireValueNode', !cfg.requireValueNode)}>
            {cfg.requireValueNode ? 'PHASE 2' : 'PHASE 1 · open'}
          </MenuToggle>
        </MenuRow>
      </MenuSection>

      <MenuActions>
        <MenuButton variant="ghost" style={{ flex: 1 }} onClick={resetAll}>RESET</MenuButton>
        <MenuButton variant="primary" onClick={onClose}>CLOSE</MenuButton>
      </MenuActions>
    </ChartMenu>
  )
}
